import { useTranslation } from 'react-i18next';
import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';

import { DetailPillStyled } from 'enrollment/features/Plans/PlanDetailsDrawer/components/PremiumDetails/PremiumDetailsStyled';
import { EmployeeContributionForm } from './EmployeeContributionForm';
import { EmployerContributionForm } from './EmployerContributionForm';
import {
  HandleAccordionValuesDto,
  ScheduledContributionDto,
} from 'enrollment/queries/useV3ContributionAmountQuery';
import { convertDate } from '../../../utils';
import { convertCurrency } from 'enrollment/utils';
import { PsAccordionChild } from 'stencilSetup';
import { hsaContributionsIntial } from 'enrollment/services';

interface EditContributionAccordionProps {
  payPeriod: ScheduledContributionDto;
  id: number;
  handleAccordionChangeValues: (
    id: number,
    handleAccordionAmounts: HandleAccordionValuesDto
  ) => void;
}

export const EditContributionAccordion: React.FC<
  EditContributionAccordionProps
> = ({ payPeriod, id, handleAccordionChangeValues }) => {
  const { t } = useTranslation('pages.financial');
  const [isExpanded, setIsExpanded] = useState(false);
  const isScreenSmall = window.innerWidth < 821;
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const { initialEmployeeContribution, initialEmployeeLumpSum } = useSelector(
    hsaContributionsIntial
  )?.scheduled_contribution?.reduce<{
    initialEmployeeContribution: number;
    initialEmployeeLumpSum: number;
  }>(
    (acc: { initialEmployeeContribution: number; initialEmployeeLumpSum: number; }, contribution: { org_payroll_schedule_id: number; scheduled_subscriber_contribution: any; scheduled_subscriber_lump_sum_contribution: any; }) => {
      if (
        contribution.org_payroll_schedule_id ===
        payPeriod.org_payroll_schedule_id
      ) {
        acc.initialEmployeeContribution = Number(
          contribution.scheduled_subscriber_contribution
        );
        acc.initialEmployeeLumpSum = Number(
          contribution.scheduled_subscriber_lump_sum_contribution
        );
      }
      return acc;
    },
    { initialEmployeeContribution: 0, initialEmployeeLumpSum: 0 }
  ) || { initialEmployeeContribution: 0, initialEmployeeLumpSum: 0 };

  useEffect(() => {
    const buttonElement = buttonRef.current;

    const handleShown = () => {
      setIsExpanded(true);
    };
    const handleHidden = () => {
      setIsExpanded(false);
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'aria-expanded') {
          const isExpanded =
            buttonElement?.getAttribute('aria-expanded') === 'true';
          if (isExpanded) {
            handleShown();
          } else {
            handleHidden();
          }
        }
      });
    });

    if (buttonElement) {
      observer.observe(buttonElement, { attributes: true });
    }

    return () => {
      if (buttonElement) {
        observer.disconnect();
      }
    };
  }, []);

  const totalContribution =
    Number(payPeriod.scheduled_employer_contribution) +
    Number(payPeriod.scheduled_employer_lump_sum_contribution) +
    Number(payPeriod.scheduled_subscriber_contribution) +
    Number(payPeriod.scheduled_subscriber_lump_sum_contribution);

  const stringId = String(id);

  return (
    <PsAccordionChild
      data-testid={`accordion-button-` + stringId}
      parent="paycheck-accordions"
      name={`accordion-child-${stringId}`}
      buttonRef={(el) => (buttonRef.current = el)}
    >
      <div slot="accordion-trigger" className="w-100">
        <div className="d-flex justify-content-between w-100 mb-2">
          <div className="mr-4">
            <strong>{convertDate(payPeriod.paycheck)}</strong>
          </div>
          <div className="mr-4">
            <strong>{convertCurrency(totalContribution.toString())}</strong>
          </div>
        </div>
        <div
          className={`pills-container ${isExpanded ? 'hidden' : ''} mb-2`}
          slot="header-content"
        >
          {Number(initialEmployeeContribution)!==0 ? (Number(payPeriod.scheduled_subscriber_contribution)===0 ? (
              <DetailPillStyled className="mr-4" $isUpdated={true}>
                {t('editContribution.noEmployeeContribution')}
              </DetailPillStyled>
            ): (
              <DetailPillStyled className="mr-4" $isUpdated={Number(initialEmployeeContribution) !== Number(payPeriod.scheduled_subscriber_contribution)}>
                {t('editContribution.employeeContribution')}
              </DetailPillStyled>
            )) : (Number(payPeriod.scheduled_subscriber_contribution)!==0 && (
              <DetailPillStyled className="mr-4" $isUpdated={true}>
                {t('editContribution.employeeContribution')}
              </DetailPillStyled>
            ))}

            {Number(initialEmployeeLumpSum)!==0 ? (Number(payPeriod.scheduled_subscriber_lump_sum_contribution)===0 ? (
              <DetailPillStyled className="mr-4" $isUpdated={true}>
                {t('editContribution.noEmployeeLumpSum')}
              </DetailPillStyled>
            ): (
              <DetailPillStyled className="mr-4" $isUpdated={Number(initialEmployeeLumpSum) !== Number(payPeriod.scheduled_subscriber_lump_sum_contribution)}>
                {t('editContribution.employeeLumpSum')}
              </DetailPillStyled>
            )) : (Number(payPeriod.scheduled_subscriber_lump_sum_contribution)!==0 && (
              <DetailPillStyled className="mr-4" $isUpdated={true}>
                {t('editContribution.employeeLumpSum')}
              </DetailPillStyled>
            ))}

            {Number(payPeriod.scheduled_employer_contribution) !== 0 && (
              <DetailPillStyled className="mr-4">
                {t('editContribution.employerContribution')}
              </DetailPillStyled>
            )

            // these conditions below can be used in DFD
            // : (
            //   <DetailPillStyled className="mr-4">
            //     {t('editContribution.noEmployerContribution')}
            //   </DetailPillStyled>
            // )
            }
            {Number(payPeriod.scheduled_employer_lump_sum_contribution) !== 0 && (
              <DetailPillStyled className="mr-4">
                {t('editContribution.employerLumpSum')}
              </DetailPillStyled>
            )
            // : (
            //   <DetailPillStyled className="mr-4">
            //     {t('editContribution.noEmployerLumpSum')}
            //   </DetailPillStyled>
            // )
            }
        </div>
      </div>

      <div slot="accordion-content" className={`${isScreenSmall ? "" : "d-flex"}`}>
        <EmployeeContributionForm
          payPeriod={payPeriod}
          id={id}
          handleAccordionChangeValues={handleAccordionChangeValues}
        />
        <EmployerContributionForm
          employerContribution={Number(
            payPeriod.scheduled_employer_contribution
          )}
          employerLumpSum={Number(
            payPeriod.scheduled_employer_lump_sum_contribution
          )}
          id={id}
        />
      </div>
    </PsAccordionChild>
  );
};
